import { jsx, jsxs } from 'react/jsx-runtime';
import { extractProductGroups } from '../StringUtils.js';
import { marked } from 'marked';
import React__default from 'react';
import { InlineCitation, InlineCitationText, InlineCitationCard, InlineCitationCardTrigger, InlineCitationCardBody, InlineCitationCarousel, InlineCitationCarouselHeader, InlineCitationCarouselPrev, InlineCitationCarouselNext, InlineCitationCarouselIndex, InlineCitationCarouselContent, InlineCitationCarouselItem, InlineCitationSource } from '../../ai-elements/inline-citation.js';
import { ProductGroupCarousel } from '../ProductGroupCarousel.js';
import { FeaturedProductWidget } from '../components/FeaturedProductWidget.js';
import { StoreLocatorWidget } from '../components/StoreLocatorWidget/index.js';
import { SurveyCarousel } from '../components/SurveyCarousel.js';
import { processProductGroupTags } from './productGroupParser.js';

// Configure marked for proper HTML rendering
marked.use({
    gfm: true,
    breaks: true,
    pedantic: false,
});
/**
 * Extract citation tags from text and replace with placeholders
 */
const extractCitationTags = (text) => {
    const citations = [];
    let citationIndex = 0;
    let currentPos = 0;
    let result = '';
    while (currentPos < text.length) {
        // Find the next opening citation tag
        const citationStartIndex = text.indexOf('<citation', currentPos);
        if (citationStartIndex === -1) {
            // No more citations, add rest of text
            result += text.substring(currentPos);
            break;
        }
        // Add text before citation tag
        result += text.substring(currentPos, citationStartIndex);
        // Find the end of the opening tag
        const tagEndIndex = text.indexOf('>', citationStartIndex);
        if (tagEndIndex === -1) {
            // Malformed tag, skip it
            result += text.substring(citationStartIndex);
            break;
        }
        // Extract the opening tag content
        const openingTag = text.substring(citationStartIndex, tagEndIndex + 1);
        // Check if this is a self-closing tag (ends with /> or / >)
        // Handle cases like <citation ... /> or <citation ... / >
        const trimmedTag = openingTag.trim();
        const isSelfClosing = trimmedTag.endsWith('/>') || trimmedTag.match(/\/\s*>$/);
        // Parse attributes from the opening tag
        const sourceMatch = openingTag.match(/source\s*=\s*"([^"]+)"/);
        const typeMatch = openingTag.match(/type\s*=\s*"([^"]+)"/);
        const titleMatch = openingTag.match(/title\s*=\s*"([^"]+)"/);
        const source = sourceMatch ? sourceMatch[1] : '';
        const type = typeMatch ? typeMatch[1] : undefined;
        const title = titleMatch ? titleMatch[1] : undefined;
        if (isSelfClosing) {
            // Handle self-closing tag: <citation ... />
            const placeholder = `@@CITATION_${citationIndex}@@`;
            citations.push({
                placeholder,
                source,
                type,
                title,
                text: title || source, // Use title or source as the text for self-closing tags
            });
            result += placeholder;
            citationIndex++;
            currentPos = tagEndIndex + 1;
            continue;
        }
        // Find the closing tag for non-self-closing tags
        const closingTagIndex = text.indexOf('</citation>', tagEndIndex + 1);
        if (closingTagIndex === -1) {
            // No closing tag found, treat as self-closing with empty text
            const placeholder = `@@CITATION_${citationIndex}@@`;
            citations.push({
                placeholder,
                source,
                type,
                title,
                text: title || source, // Use title or source as the text
            });
            result += placeholder;
            citationIndex++;
            currentPos = tagEndIndex + 1;
            continue;
        }
        // Extract the text content between tags
        const citationText = text.substring(tagEndIndex + 1, closingTagIndex);
        // Create placeholder
        const placeholder = `@@CITATION_${citationIndex}@@`;
        citations.push({
            placeholder,
            source,
            type,
            title,
            text: citationText,
        });
        result += placeholder;
        citationIndex++;
        // Move past the closing tag
        currentPos = closingTagIndex + 11; // "</citation>".length
    }
    return {
        textWithPlaceholders: result,
        citations,
    };
};
/**
 * Render message content with product group carousels, featured products, and survey cards embedded
 */
const renderMessageContentWithCarousels = (text, productMetadataMap, streamingGroups, isStreaming = false, isAlreadyProcessed = false, onVariantSelect, surveyMetadataMap) => {
    // Extract citation tags and replace with placeholders BEFORE processing
    const { textWithPlaceholders: textWithCitations, citations } = extractCitationTags(text);
    // Extract featured product tags and replace with placeholders BEFORE processing
    // This preserves their positions in the message
    // Handle both self-closing tags (/>) and tags with optional closing slash
    // Also handle flexible whitespace (including newlines) around attributes
    const featuredProductRegex = /<product\s+featured\s*=\s*"true"\s+stacklineSku\s*=\s*"([^"]+)"(?:\s+question\s*=\s*"([^"]+)")?\s*\/?>/g;
    const featuredProducts = [];
    let featuredProductIndex = 0;
    let textWithFeaturedProductPlaceholders = textWithCitations.replace(featuredProductRegex, (match, stacklineSku, question) => {
        // Use a placeholder format that markdown won't interpret
        // Using @@ which markdown doesn't interpret
        const placeholder = `@@FEATURED_PRODUCT_${featuredProductIndex}@@`;
        featuredProducts.push({
            placeholder,
            stacklineSku,
            question: question || '',
        });
        featuredProductIndex++;
        return placeholder;
    });
    // Extract store locator tags and replace with placeholders
    // Handles: <store_locator stacklineSku="ABC123" /> and <store_locator stacklineSku="ABC123"></store_locator>
    const storeLocatorRegex = /<store_locator\s+stacklineSku\s*=\s*"([^"]+)"\s*\/?>/g;
    const storeLocators = [];
    let storeLocatorIndex = 0;
    textWithFeaturedProductPlaceholders = textWithFeaturedProductPlaceholders.replace(storeLocatorRegex, (match, stacklineSku) => {
        const placeholder = `@@STORE_LOCATOR_${storeLocatorIndex}@@`;
        storeLocators.push({
            placeholder,
            stacklineSku,
        });
        storeLocatorIndex++;
        return placeholder;
    });
    // Extract survey tags and group consecutive ones into carousels
    // This respects LLM's organization (surveys under same header = one carousel)
    // Handles: <survey campaignId="ABC123">...</survey> and <survey campaignId="ABC123" />
    // Find groups of consecutive survey tags (separated only by whitespace/newlines)
    const surveyGroupRegex = /(?:<survey\s+campaignId\s*=\s*"[^"]+"[^>]*>[\s\S]*?<\/survey>|<survey\s+campaignId\s*=\s*"[^"]+"\s*\/>)(?:\s*(?:<survey\s+campaignId\s*=\s*"[^"]+"[^>]*>[\s\S]*?<\/survey>|<survey\s+campaignId\s*=\s*"[^"]+"\s*\/>))*/g;
    const surveyGroups = [];
    let surveyGroupIndex = 0;
    const textWithSurveyPlaceholders = textWithFeaturedProductPlaceholders.replace(surveyGroupRegex, (groupMatch) => {
        // Extract all campaign IDs from this group
        const campaignIds = [];
        let match;
        const localRegex = /<survey\s+campaignId\s*=\s*"([^"]+)"[^>]*>[\s\S]*?<\/survey>|<survey\s+campaignId\s*=\s*"([^"]+)"\s*\/>/g;
        while ((match = localRegex.exec(groupMatch)) !== null) {
            campaignIds.push(match[1] || match[2]);
        }
        const placeholder = `@@SURVEY_GROUP_${surveyGroupIndex}@@`;
        surveyGroups.push({ placeholder, campaignIds });
        surveyGroupIndex++;
        return placeholder;
    });
    // Process product group tags BEFORE markdown parsing (if not already processed)
    // For streaming messages, text is already processed with |||PRODUCT_GROUP_N||| placeholders
    // For loaded messages, we need to process the raw <product_group> tags first
    const processedText = isAlreadyProcessed
        ? textWithSurveyPlaceholders
        : processProductGroupTags(textWithSurveyPlaceholders);
    // Now parse markdown
    const htmlContent = marked.parse(processedText);
    // Remove <hr> elements from the parsed HTML
    const htmlContentWithoutHr = htmlContent.replace(/<hr\s*\/?>/gi, '');
    // Wrap tables in scrollable containers for mobile responsiveness
    const htmlContentWithWrappedTables = htmlContentWithoutHr.replace(/<table[\s\S]*?<\/table>/gi, (match) => `<div class="table-scroll-wrapper">${match}</div>`);
    // Split by product group placeholders, featured product placeholders, store locator placeholders, survey group placeholders, AND citation placeholders
    // NOTE: pipes must be escaped in the regex, otherwise the split matches empty strings
    // The regex uses capturing groups so the placeholders are included in the split results
    const parts = htmlContentWithWrappedTables.split(/(\|\|\|PRODUCT_GROUP_\d+\|\|\||@@FEATURED_PRODUCT_\d+@@|@@STORE_LOCATOR_\d+@@|@@SURVEY_GROUP_\d+@@|@@CITATION_\d+@@)/);
    // Extract product groups from the original text to get stacklineSku references
    const productGroups = extractProductGroups(text);
    return parts.map((part, idx) => {
        var _a, _b;
        // Check if this part is a product group placeholder
        const groupMatch = part.match(/\|\|\|PRODUCT_GROUP_(\d+)\|\|\|/);
        if (groupMatch) {
            const groupIndex = parseInt(groupMatch[1]);
            // For streaming: use streamingGroups if available, otherwise extract from text
            // For loaded: always extract from text
            let allStacklineSkus;
            if (isStreaming && streamingGroups.has(groupIndex)) {
                allStacklineSkus = streamingGroups.get(groupIndex) || [];
            }
            else {
                const group = productGroups[groupIndex];
                allStacklineSkus = (group === null || group === void 0 ? void 0 : group.products.map((p) => p.stacklineSku)) || [];
            }
            // Map stacklineSkus to actual product metadata from the centralized map
            const groupProductsWithMetadata = allStacklineSkus
                .map((sku) => productMetadataMap.get(sku))
                .filter(Boolean);
            return (jsx(ProductGroupCarousel, { products: groupProductsWithMetadata, allStacklineSkus: allStacklineSkus, isLoading: isStreaming && allStacklineSkus.length === 0 }, `group-${idx}`));
        }
        // Check if this part is a featured product placeholder
        const featuredMatch = part.match(/@@FEATURED_PRODUCT_(\d+)@@/);
        if (featuredMatch) {
            const featuredIndex = parseInt(featuredMatch[1]);
            const featuredProduct = featuredProducts[featuredIndex];
            if (featuredProduct) {
                const product = productMetadataMap.get(featuredProduct.stacklineSku);
                if (product) {
                    return (jsx(FeaturedProductWidget, { product: product, onVariantSelect: onVariantSelect }, `featured-${idx}-${featuredProduct.stacklineSku}`));
                }
                else {
                    // Product metadata not available - this usually means the API didn't return product data for this SKU
                    // Log a warning for debugging but don't render a widget
                    if (process.env.NODE_ENV === 'development') {
                        console.warn(`FeaturedProductWidget: Product metadata not found for SKU: ${featuredProduct.stacklineSku}. ` +
                            `Available SKUs: ${Array.from(productMetadataMap.keys()).join(', ')}`);
                    }
                }
            }
            // If product not found, return empty fragment
            return jsx(React__default.Fragment, {}, `featured-missing-${idx}`);
        }
        // Check if this part is a store locator placeholder
        const storeLocatorMatch = part.match(/@@STORE_LOCATOR_(\d+)@@/);
        if (storeLocatorMatch) {
            const storeLocatorIdx = parseInt(storeLocatorMatch[1]);
            const storeLocator = storeLocators[storeLocatorIdx];
            if (storeLocator) {
                return (jsx(StoreLocatorWidget, { stacklineSku: storeLocator.stacklineSku }, `store-locator-${idx}-${storeLocator.stacklineSku}`));
            }
            // If store locator not found, return empty fragment
            return jsx(React__default.Fragment, {}, `store-locator-missing-${idx}`);
        }
        // Check if this part is a survey group placeholder
        const surveyGroupMatch = part.match(/@@SURVEY_GROUP_(\d+)@@/);
        if (surveyGroupMatch) {
            const groupIdx = parseInt(surveyGroupMatch[1]);
            const group = surveyGroups[groupIdx];
            if (group && surveyMetadataMap) {
                // Collect all survey metadata for this group
                const surveysWithMetadata = [];
                for (const campaignId of group.campaignIds) {
                    const surveyData = surveyMetadataMap.get(campaignId);
                    if (surveyData) {
                        surveysWithMetadata.push(surveyData);
                    }
                    else if (process.env.NODE_ENV === 'development') {
                        console.warn(`SurveyCarousel: Survey metadata not found for campaignId: ${campaignId}. ` +
                            `Available campaignIds: ${Array.from(surveyMetadataMap.keys()).join(', ')}`);
                    }
                }
                if (surveysWithMetadata.length > 0) {
                    return jsx(SurveyCarousel, { surveys: surveysWithMetadata }, `survey-group-${idx}`);
                }
            }
            // If no surveys found, return empty fragment
            return jsx(React__default.Fragment, {}, `survey-group-empty-${idx}`);
        }
        // Check if this part is a citation placeholder
        // The placeholder might be standalone or wrapped in HTML tags from markdown parsing
        const citationMatch = part.match(/@@CITATION_(\d+)@@/);
        if (citationMatch) {
            const citationIndex = parseInt(citationMatch[1]);
            const citation = citations[citationIndex];
            if (citation && citation.source) {
                // Use title or source as fallback if text is empty or whitespace
                const displayText = ((_a = citation.text) === null || _a === void 0 ? void 0 : _a.trim()) || citation.title || citation.source;
                // Create a source object for the InlineCitation component
                const source = {
                    title: citation.title || citation.source,
                    url: citation.source,
                    description: ((_b = citation.text) === null || _b === void 0 ? void 0 : _b.trim()) || undefined,
                };
                return (jsxs(InlineCitation, { children: [jsx(InlineCitationText, Object.assign({ url: citation.source }, { children: displayText })), jsxs(InlineCitationCard, { children: [jsx(InlineCitationCardTrigger, { sources: [citation.source] }), jsx(InlineCitationCardBody, { children: jsxs(InlineCitationCarousel, { children: [jsxs(InlineCitationCarouselHeader, { children: [jsx(InlineCitationCarouselPrev, {}), jsx(InlineCitationCarouselNext, {}), jsx(InlineCitationCarouselIndex, {})] }), jsx(InlineCitationCarouselContent, { children: jsx(InlineCitationCarouselItem, { children: jsx(InlineCitationSource, { description: source.description, title: source.title, url: source.url }) }) })] }) })] })] }, `citation-${idx}`));
            }
            // If citation not found or invalid, return empty fragment
            return jsx(React__default.Fragment, {}, `citation-missing-${idx}`);
        }
        // Regular content - use dangerouslySetInnerHTML to render HTML
        // Skip empty parts (they might be empty strings from splitting)
        if (!part.trim()) {
            return jsx(React__default.Fragment, {}, `empty-${idx}`);
        }
        return (jsx("div", { style: { display: 'flex', flexDirection: 'column' }, dangerouslySetInnerHTML: { __html: part }, className: "message-markdown" }, idx));
    });
};

export { extractCitationTags, renderMessageContentWithCarousels };
//# sourceMappingURL=messageRenderer.js.map
